import { ContainerBloco, LinhaInfo, LinhaInfoInline, TituloBloco } from "../../../Common/styles";
import { IProposta } from "../../../../../utility/IProposta";
import { SectionDivider } from "../../../../CardDemanda/CabecalhoCardDemanda/styles";
import { Mail, MapPin, MapPinned, Monitor, Pin } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import InputEditavel from "../../../../form/InputEditavel";
import { useCallback } from "react";
import { useCepLookup } from "../../../../../hooks/useCepLookup";
import { formatarCep } from "../../../../../utility/formatadorCep";

interface BlocoEnderecoProps {
    demanda?: IProposta | null;
    loading?: boolean;
    isEditing?: boolean;
    isDisabled?: boolean;
    onChange?: (campo: string, valor: any) => void;
    onBlur?: (campo: string) => void;
    errors?: Record<string, string>;
}

export default function BlocoEndereco({demanda, loading = false, isEditing = false, isDisabled = false, onChange, onBlur, errors}: BlocoEnderecoProps) {
    const { loadingCep, buscarCep } = useCepLookup();

    const handleCepChange = useCallback((value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (onChange) {
            onChange('cep', numbers);
        }
    }, [onChange]);

    const handleCepBlur = useCallback(() => {
        const cepLimpo = (demanda?.cep || '').replace(/\D/g, '');
        
        if (cepLimpo.length === 8) {
            buscarCep(cepLimpo, (data) => {
                if (onChange) {
                    if (!demanda?.rua || demanda.rua.trim() === '') {
                        onChange('rua', data.logradouro || '');
                    }
                    if (!demanda?.bairro || demanda.bairro.trim() === '') {
                        onChange('bairro', data.bairro || '');
                    }
                    if ((!demanda?.uf_comarca || demanda.uf_comarca.trim() === '') && data.uf) {
                        onChange('uf_comarca', data.uf);
                    }
                    if ((!demanda?.cidade_comarca || demanda.cidade_comarca.trim() === '') && data.localidade) {
                        onChange('cidade_comarca', data.localidade);
                    }
                }
            });
        }
        
        if (onBlur) {
            onBlur('cep');
        }
    }, [demanda, buscarCep, onChange, onBlur]);
    return (
        <ContainerBloco>
            {loading ? (
                <>
                    <Skeleton height={24} width="60%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" />
                </>
            ) : (
                <>
                <TituloBloco>Endereço</TituloBloco>
                <>
                    {isEditing ? (
                        <>
                            <LinhaInfo>
                                <span className="label">
                                    <Mail size={16} color="#8A99A8"/>
                                CEP:
                            </span>
                            <InputEditavel
                                value={demanda?.cep}
                                onChange={handleCepChange}
                                onBlur={handleCepBlur}
                                isEditing={isEditing}
                                placeholder="00000-000"
                                mask="99999-999"
                                disabled={isDisabled || loadingCep}
                                error={errors?.cep}
                            />
                        </LinhaInfo>
                        <LinhaInfo>
                            <span className="label">
                                <MapPinned size={16} color="#8A99A8"/>
                            Rua:
                        </span>
                            <InputEditavel
                                value={demanda?.rua}
                                onChange={(value) => onChange && onChange('rua', value)}
                                isEditing={isEditing}
                                disabled={isDisabled || loadingCep}
                                placeholder="Rua"
                            />
                        </LinhaInfo>
                        <LinhaInfo>
                            <span className="label">
                                <Pin size={16} color="#8A99A8"/>
                            Número:
                        </span>
                            <InputEditavel
                                value={demanda?.numero}
                                onChange={(value) => onChange && onChange('numero', value)}
                                isEditing={isEditing}
                                disabled={isDisabled}
                                placeholder="Número"
                            />
                        </LinhaInfo>
                        <LinhaInfo>
                            <span className="label">
                                <MapPin size={16} color="#8A99A8"/>
                            Bairro:
                        </span>
                            <InputEditavel
                                value={demanda?.bairro}
                                onChange={(value) => onChange && onChange('bairro', value)}
                                isEditing={isEditing}
                                disabled={isDisabled || loadingCep}
                                placeholder="Bairro"
                            />
                        </LinhaInfo>
                        <LinhaInfo>
                            <span className="label">
                                <Pin size={16} color="#8A99A8"/>
                            Complemento:	
                        </span>
                            <InputEditavel
                                value={demanda?.complemento}
                                onChange={(value) => onChange && onChange('complemento', value)}
                                isEditing={isEditing}
                                disabled={isDisabled}
                                placeholder="Complemento"
                            />
                        </LinhaInfo>
                        </>
                    ) : (
                        <>
                            <LinhaInfoInline>
                                <span className="label">
                                    <MapPinned size={16} color="#8A99A8"/>
                                Endereço:
                            </span>
                                <span className="value">
                                    {[demanda?.rua, demanda?.numero].filter(Boolean).join(', ') || 'N/A'}
                                </span>
                            </LinhaInfoInline>
                            <LinhaInfoInline>
                                <span className="label">
                                    <Pin size={16} color="#8A99A8"/>
                                Complemento:	
                            </span>
                                <span className="value">
                                    {demanda?.complemento || 'N/A'}
                                </span>
                            </LinhaInfoInline>
                            <LinhaInfoInline>
                                <span className="label">
                                    <MapPin size={16} color="#8A99A8"/>
                                Bairro:
                            </span>
                                <span className="value">
                                    {demanda?.bairro || 'N/A'}
                                </span>
                            </LinhaInfoInline>
                            <LinhaInfoInline>
                                <span className="label">
                                    <Mail size={16} color="#8A99A8"/>
                                CEP:
                            </span>
                                <span className="value">
                                    {formatarCep(demanda?.cep)}
                                </span>
                            </LinhaInfoInline>
                        </>
                    )}
                {
                    (demanda?.modalidade === 'online' || demanda?.modalidade === 'Virtual') &&
                        <>
                        <SectionDivider/>
                        <br/>
                        <LinhaInfo>
                            <span className="label">
                                <Monitor size={16} color="#8A99A8"/>
                        Endereço Virtual:
                    </span>
                            <InputEditavel
                                value={demanda?.endereco_virtual}
                                onChange={(value) => onChange && onChange('endereco_virtual', value)}
                                onBlur={() => onBlur && onBlur('endereco_virtual')}
                                isEditing={isEditing}
                                disabled={isDisabled}
                                placeholder="URL do endereço virtual"
                                error={errors?.endereco_virtual}
                            />
                        </LinhaInfo>
                        </>
                    }
                    </>
                </>
            )}
        </ContainerBloco>
    )
}
